import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { webhookAPI, driveAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Plug, HardDrive, Sparkles, Mail, Webhook, Plus, Trash2, ExternalLink, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

import axios from 'axios';
import { deriveSmtpTone } from '../components/EmailHealthCard';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const WEBHOOK_EVENTS = [
  { id: 'task_created', label: 'Task Created' },
  { id: 'task_updated', label: 'Task Updated' },
  { id: 'task_completed', label: 'Task Completed' },
  { id: 'task_assigned', label: 'Task Assigned' },
];

export default function IntegrationsHubPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [hub, setHub] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  // D65 P1-5 — live SMTP health drives the Email Notifications card pill.
  // Owner-only endpoint; admins gracefully see "Unknown" pill.
  const [emailHealth, setEmailHealth] = useState(null);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [whForm, setWhForm] = useState({ name: '', url: '', events: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    Promise.all([
      webhookAPI.hub(currentWorkspace.id),
      webhookAPI.list(currentWorkspace.id),
    ]).then(([hRes, wRes]) => { setHub(hRes.data); setWebhooks(wRes.data); }).catch(console.error).finally(() => setLoading(false));
    // D65 P1-5 — fetch live email health for the Integrations card pill.
    // Endpoint is owner-only; admins get 403 and we render "Unknown".
    (async () => {
      try {
        const token = localStorage.getItem('stellar_token');
        const r = await axios.get(`${API}/settings/email-health`, { headers: { Authorization: `Bearer ${token}` } });
        setEmailHealth(r.data);
      } catch { setEmailHealth(null); }
    })();
  }, [currentWorkspace?.id]);

  const handleAddWebhook = async () => {
    if (!whForm.name.trim() || !whForm.url.trim()) return toast.error('Name and URL required');
    setSaving(true);
    try {
      await webhookAPI.create({ workspace_id: currentWorkspace.id, ...whForm });
      toast.success('Webhook added');
      setShowAddWebhook(false);
      setWhForm({ name: '', url: '', events: [] });
      const res = await webhookAPI.list(currentWorkspace.id);
      setWebhooks(res.data);
    } catch (err) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const toggleEvent = (eventId) => {
    setWhForm(f => ({ ...f, events: f.events.includes(eventId) ? f.events.filter(e => e !== eventId) : [...f.events, eventId] }));
  };

  const deleteWebhook = async (id) => {
    await webhookAPI.delete(id);
    setWebhooks(prev => prev.filter(w => w.id !== id));
    toast.success('Deleted');
  };

  const toggleWebhook = async (id) => {
    const res = await webhookAPI.toggle(id);
    setWebhooks(prev => prev.map(w => w.id === id ? { ...w, active: res.data.active } : w));
  };

  if (loading) return <div className="max-w-4xl mx-auto p-8 space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/20 rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6" data-testid="integrations-hub">
      <div>
        <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight">Integrations</h1>
        <p className="text-[12px] text-muted-foreground mt-0.5">Connect third-party apps and services</p>
      </div>

      {/* Connected Services */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Google Drive */}
        <Card className="card-hover-gold">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-foreground/[0.03] border border-border/50 flex items-center justify-center shrink-0">
              <HardDrive className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold">Google Drive</p>
              <p className="text-[11px] text-muted-foreground">Browse & attach files from Drive</p>
            </div>
            {hub?.google_drive?.connected ? (
              <div className="flex items-center gap-2">
                <Badge className="text-[9px] bg-green-500/10 text-green-500/70 border-green-500/20">Connected</Badge>
                <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={async () => { await driveAPI.disconnect(); toast.success('Disconnected'); setHub(h => ({ ...h, google_drive: { connected: false } })); }}>Disconnect</Button>
              </div>
            ) : (
              <Button size="sm" className="h-8 text-[11px]" onClick={async () => { try { const res = await driveAPI.connect(); window.open(res.data.authorization_url, '_blank'); } catch { toast.error('Failed'); } }}>Connect</Button>
            )}
          </CardContent>
        </Card>

        {/* AI Assistant */}
        <Card className="card-hover-gold">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-foreground/[0.03] border border-border/50 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold">AI Assistant</p>
              <p className="text-[11px] text-muted-foreground">{hub?.ai_assistant?.model || 'GPT-4.1 Mini'}</p>
            </div>
            <Badge className="text-[9px] bg-green-500/10 text-green-500/70 border-green-500/20">Active</Badge>
          </CardContent>
        </Card>

        {/* Email — D65 P1-5: pill reads live SMTP health. */}
        <Card className="card-hover-gold">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-foreground/[0.03] border border-border/50 flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold">Email Notifications</p>
              <p className="text-[11px] text-muted-foreground">{hub?.email?.provider || 'SMTP'}</p>
            </div>
            {(() => {
              if (!emailHealth) {
                return <Badge className="text-[9px] bg-foreground/[0.05] text-muted-foreground/60 border-border/30" data-testid="integrations-email-pill">Unknown</Badge>;
              }
              const { tone, label } = deriveSmtpTone(emailHealth);
              const cls = {
                green: 'bg-emerald-500/10 text-emerald-500/80 border-emerald-500/20',
                amber: 'bg-amber-500/15 text-amber-400/80 border-amber-500/30',
                red:   'bg-rose-500/15 text-rose-400/80 border-rose-500/30',
                grey:  'bg-foreground/[0.05] text-muted-foreground/60 border-border/30',
              }[tone];
              return <Badge className={`text-[9px] ${cls}`} data-testid="integrations-email-pill" title={label}>{label}</Badge>;
            })()}
          </CardContent>
        </Card>

        {/* Zapier / Make */}
        <Card className="card-hover-gold">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-foreground/[0.03] border border-border/50 flex items-center justify-center shrink-0">
              <Plug className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold">Zapier / Make.com</p>
              <p className="text-[11px] text-muted-foreground">Connect 6,000+ apps via webhooks</p>
            </div>
            <a href="https://zapier.com" target="_blank" rel="noopener noreferrer" className="text-[11px] text-gold hover:underline flex items-center gap-1">Setup <ExternalLink className="h-3 w-3" /></a>
          </CardContent>
        </Card>

        {/* Notion Import */}
        <Card className="card-hover-gold cursor-pointer" onClick={() => window.location.href = '/tasks/notion-import'} data-testid="notion-integration-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-foreground/[0.03] border border-border/50 flex items-center justify-center shrink-0">
              <svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h6l2 2h8v14H4z" /><path d="M8 8h8M8 12h6" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold">Notion Import</p>
              <p className="text-[11px] text-muted-foreground">Import databases from Notion as tasks</p>
            </div>
            <span className="text-[11px] text-gold">Import →</span>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Webhooks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-[14px] font-semibold">Webhooks</h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">Send real-time events to external services (Zapier, Make, Slack, custom APIs)</p>
          </div>
          <Button size="sm" onClick={() => setShowAddWebhook(true)} data-testid="add-webhook-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Webhook
          </Button>
        </div>

        {webhooks.length === 0 ? (
          <Card className="card-hover-gold"><CardContent className="py-10 text-center text-muted-foreground/40 text-[13px]">
            <Webhook className="h-8 w-8 mx-auto mb-2 opacity-20" />
            No webhooks configured. Add one to connect with Zapier, Make, or Slack.
          </CardContent></Card>
        ) : (
          <div className="space-y-1.5">
            {webhooks.map(wh => (
              <Card key={wh.id} className="card-hover-gold">
                <CardContent className="p-3 flex items-center gap-3">
                  <Webhook className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium">{wh.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{wh.url}</p>
                    {wh.events?.length > 0 && (
                      <div className="flex gap-1 mt-1">{wh.events.map(e => <Badge key={e} variant="outline" className="text-[8px]">{e.replace('_', ' ')}</Badge>)}</div>
                    )}
                  </div>
                  <Switch checked={wh.active} onCheckedChange={() => toggleWebhook(wh.id)} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteWebhook(wh.id)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground/40" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* How to connect guide */}
      <Card className="card-hover-gold">
        <CardContent className="p-4">
          <h3 className="text-[13px] font-semibold mb-2">How to connect with Zapier or Make</h3>
          <ol className="text-[12px] text-muted-foreground space-y-1.5 list-decimal list-inside">
            <li>Create a webhook in Zapier (Trigger: "Webhooks by Zapier" → "Catch Hook") or Make ("Webhooks" → "Custom Webhook")</li>
            <li>Copy the webhook URL they give you</li>
            <li>Click "Add Webhook" above and paste the URL</li>
            <li>Select which events to send (task created, updated, completed)</li>
            <li>In Zapier/Make, set up your action (e.g., send to Notion, Slack, Google Sheets)</li>
          </ol>
        </CardContent>
      </Card>

      {/* Add Webhook Dialog */}
      <Dialog open={showAddWebhook} onOpenChange={setShowAddWebhook}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add Webhook</DialogTitle><DialogDescription>Send task events to external services in real-time.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1"><Label className="text-[12px]">Name</Label><Input placeholder="e.g. Zapier - New Tasks to Notion" value={whForm.name} onChange={e => setWhForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label className="text-[12px]">Webhook URL</Label><Input placeholder="https://hooks.zapier.com/..." value={whForm.url} onChange={e => setWhForm(f => ({ ...f, url: e.target.value }))} /></div>
            <div className="space-y-1.5">
              <Label className="text-[12px]">Events to send <span className="text-muted-foreground/40">(leave empty for all events)</span></Label>
              <div className="space-y-1">
                {WEBHOOK_EVENTS.map(ev => (
                  <label key={ev.id} className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${whForm.events.includes(ev.id) ? 'bg-gold/[0.06]' : 'hover:bg-foreground/[0.03]'}`}>
                    <Checkbox checked={whForm.events.includes(ev.id)} onCheckedChange={() => toggleEvent(ev.id)} />
                    <span className="text-[12px]">{ev.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowAddWebhook(false)}>Cancel</Button><Button onClick={handleAddWebhook} disabled={saving}>{saving ? 'Adding...' : 'Add Webhook'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
